import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, Check } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { UI } from "@/i18n";
import { fileUrl } from "@/lib/api";
import { scrollToId } from "@/App";
import { easeOut } from "./Reveal";

export default function Hero() {
  const { content, t } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const lines = content?.hero?.title_lines || [];

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden pt-28 pb-16">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1682718619781-252f23e21132?crop=entropy&cs=srgb&fm=jpg&q=80"
          alt=""
          className="kenburns w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(11,19,43,0.92)_0%,rgba(11,19,43,0.75)_45%,rgba(11,19,43,0.97)_100%)]" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(60% 50% at 75% 40%, rgba(46,155,232,0.14) 0%, transparent 70%)" }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: easeOut }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="stitch-x w-10 text-brand-orange" />
            <span className="font-mono2 text-xs sm:text-sm uppercase tracking-[0.3em] text-brand-orange">
              {t(content?.hero?.eyebrow)}
            </span>
          </motion.div>

          <h1 className="font-display uppercase leading-[0.95] text-[clamp(2.7rem,7.5vw,6.2rem)]">
            {lines.map((l, i) => (
              <span className="line-mask" key={i}>
                <motion.span
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.2 + i * 0.14, duration: 0.9, ease: easeOut }}
                  className={i === lines.length - 1 ? "text-brand-orange" : ""}
                >
                  {t(l)}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7, ease: easeOut }}
            className="mt-6 max-w-xl text-base sm:text-lg text-[#B9C4D8] leading-relaxed"
          >
            {t(content?.hero?.subtitle)}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7, ease: easeOut }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("contact")}
              data-testid="hero-quote-btn"
              className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-cobalt text-white font-bold px-7 py-4 rounded-full transition-all hover:scale-[1.03] shadow-[0_16px_40px_-12px_rgba(46,155,232,0.6)]"
            >
              {t(content?.hero?.cta_primary)}
              <ArrowDown size={17} />
            </button>
            <a
              href={`tel:${content?.phone}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-3 border-2 border-brand-red/80 hover:border-brand-red text-white font-semibold px-6 py-3.5 rounded-full transition-all hover:bg-brand-red/10"
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-red">
                <Phone size={15} />
              </span>
              <span className="font-mono2 tracking-wide">{content?.phone}</span>
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
          >
            {(content?.hero?.badges || []).map((b, i) => (
              <li key={i} className="flex items-center gap-2 text-xs sm:text-sm font-semibold uppercase tracking-wider text-white/75">
                <Check size={14} className="text-brand-orange" strokeWidth={3} />
                {t(b)}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div style={{ y: yLogo }} className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotate: -2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.45, duration: 1, ease: easeOut }}
            className="relative"
          >
            <div className="stitch-border rounded-2xl p-3 bg-brand-cobalt/20 backdrop-blur-sm shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]">
              <img
                src={fileUrl(content?.logo_url)}
                alt="CT Upholstery — Manufacturing and Repair"
                data-testid="hero-logo"
                className="w-full rounded-xl border border-white/10"
              />
            </div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="absolute -top-5 -left-4 sm:-left-8 bg-brand-navy border border-white/15 rounded-xl px-4 py-3 shadow-xl"
            >
              <p className="font-display text-2xl text-brand-orange leading-none">{content?.stats?.[0]?.value || "15+"}</p>
              <p className="text-[10px] font-mono2 uppercase tracking-widest text-white/70 mt-1">{t(content?.stats?.[0]?.label)}</p>
            </motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 0.6 }}
              className="absolute -bottom-5 -right-3 sm:-right-6 bg-brand-navy border border-white/15 rounded-xl px-4 py-3 shadow-xl"
            >
              <p className="font-display text-2xl text-brand-blue leading-none">{content?.stats?.[2]?.value || "100%"}</p>
              <p className="text-[10px] font-mono2 uppercase tracking-widest text-white/70 mt-1">{t(content?.stats?.[2]?.label)}</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
}
